import { useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { Checkbox } from "antd";

const useTableRowSelection = ({
                                listSelector,
                                selectedRowsSelector,
                                setSelectedRowsAction,
                                clearSelectedRowsAction,
                                rowKeyExtractor,
                                filteredDataSource,
                              }) => {
  const dispatch = useDispatch();
  const list = useSelector(listSelector);
  const selectedRows = useSelector(selectedRowsSelector);
  const preserveSelection = useRef(true);
  const lastValidFilteredDataSource = useRef([]);

  const getFilteredRowKeys = () => {
    if (filteredDataSource == null) {
      return new Set(lastValidFilteredDataSource.current.map(rowKeyExtractor));
    } else {
      lastValidFilteredDataSource.current = filteredDataSource;
      return new Set(filteredDataSource.map(rowKeyExtractor));
    }
  };

  const updateSelectedKeys = (keysUpdater) =>
    dispatch(setSelectedRowsAction(keysUpdater(selectedRows)));

  const updateCheckboxState = () => {
    const filteredRowKeys = getFilteredRowKeys();
    const filteredSelectedCount = selectedRows.filter((key) =>
      filteredRowKeys.has(key)
    ).length;
    const filteredTotalCount = filteredRowKeys.size;

    return {
      indeterminate: filteredTotalCount > 0 && filteredSelectedCount > 0 && filteredSelectedCount < filteredTotalCount,
      checked: filteredTotalCount > 0 && filteredSelectedCount > 0 && filteredSelectedCount === filteredTotalCount,
      disableSelectAll: filteredTotalCount === 0
    };
  };

  const { indeterminate, checked, disableSelectAll  } = updateCheckboxState();

  const onRowSelectionChange = (selectedRowKeys) => {
    dispatch(setSelectedRowsAction(selectedRowKeys));
  };

  const doNotPreserveSelections = () => {
    preserveSelection.current = false;
  };

  const onSelectAll = (selected) => {
    const filteredRowKeys = getFilteredRowKeys();
    updateSelectedKeys((currentKeys) =>
      selected
        ? Array.from(new Set([...currentKeys, ...filteredRowKeys]))
        : currentKeys.filter((key) => !filteredRowKeys.has(key))
    );
  };

  const filteredSelectedRowsCount = useMemo(() => {
    const filteredRowKeys = getFilteredRowKeys();
    return selectedRows.filter((key) => filteredRowKeys.has(key)).length;
  }, [filteredDataSource, selectedRows]);

  const selectedRowRecords = useMemo(() => {
    if (!list?.length || !selectedRows?.length) return [];

    const lookup = Object.create(null);
    selectedRows.forEach(k => {
      lookup[k] = true;
    });

    return list.filter(row => lookup[rowKeyExtractor(row)]);
  }, [list, selectedRows, rowKeyExtractor]);

  useEffect(() => {
    if (isEmpty(list)) {
      dispatch(clearSelectedRowsAction());
      return;
    }
    const listRowKeys = new Set(list.map(rowKeyExtractor));
    updateSelectedKeys((currentKeys) =>
      currentKeys.filter((key) => listRowKeys.has(key))
    );
  }, [list]);

  useEffect(() => {
    if (list === null || preserveSelection.current) return;
    dispatch(clearSelectedRowsAction());
    preserveSelection.current = true;
  }, [list]);

  useEffect(() => {
    dispatch(clearSelectedRowsAction());
  }, []);

  const customColumnTitle = (
    <Checkbox
      indeterminate={indeterminate}
      checked={checked}
      onChange={(e) => onSelectAll(e.target.checked)}
      disabled={disableSelectAll}
    />
  );

  return {
    list,
    selectedRows,
    selectedRowRecords,
    doNotPreserveSelections,
    rowKeyExtractor,
    filteredSelectedRowsCount,
    rowSelection: {
      columnWidth: 30,
      selectedRowKeys: selectedRows,
      onChange: onRowSelectionChange,
      fixed: true,
      hideSelectAll: true,
      columnTitle: customColumnTitle,
    },
  };
};

export default useTableRowSelection;
